import { buildPermission } from "portal/utils/auth";
import { ComponentType, FC, ReactNode } from "react";
import { FeatureFlag, useFeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import {
  plantProfilesAnyUpdateStatement,
  plantProfilesPageReadStatement,
  plantProfilesTemplatesUpdateStatement,
} from "portal/views/plantProfiles/permissions";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { withFeatureFlag } from "portal/components/RequireFeatureFlag";

// List categories also runs ResolveCrossCustomerReadAccess in the BE, which requires
// read:customers
const customerReadStatement = {
  or: [
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    ),
  ],
};

const categoryTemplatesPageReadStatement = {
  and: [plantProfilesPageReadStatement, customerReadStatement],
};

export const useCanUpdateCategoryTemplate = (): boolean =>
  useAuthorizationRequired(plantProfilesAnyUpdateStatement);

export const useCanUpdateCarbonCategoryTemplates = (): boolean =>
  useAuthorizationRequired(plantProfilesTemplatesUpdateStatement);

export const useCanSeeCategoryTemplatesPage = (): boolean => {
  const { isEnabled: hasCustomCategories } = useFeatureFlag(
    FeatureFlag.CUSTOM_CATEGORIES
  );
  const isCategoryTemplatesAuthorized = useAuthorizationRequired(
    categoryTemplatesPageReadStatement
  );
  return Boolean(isCategoryTemplatesAuthorized && hasCustomCategories);
};

export const withCategoryTemplatesReadAccess = <P extends object>(
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FC<P> =>
  withAuthenticationRequired(
    withFeatureFlag(
      { flag: FeatureFlag.CUSTOM_CATEGORIES, noFlag: Path.FLEET },
      withAuthorizationRequired(
        categoryTemplatesPageReadStatement,
        Component,
        otherwise
      )
    )
  );

export const useCanUpdateCategoryTemplatesPage = (): boolean => {
  const { isEnabled: hasCustomCategories } = useFeatureFlag(
    FeatureFlag.CUSTOM_CATEGORIES
  );
  const isCategoryTemplatesAuthorized = useAuthorizationRequired(
    plantProfilesAnyUpdateStatement
  );
  return Boolean(isCategoryTemplatesAuthorized && hasCustomCategories);
};

export const withCategoryTemplatesUpdateAccess = <P extends object>(
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FC<P> =>
  withAuthenticationRequired(
    withFeatureFlag(
      { flag: FeatureFlag.CUSTOM_CATEGORIES, noFlag: Path.FLEET },
      withAuthorizationRequired(
        plantProfilesAnyUpdateStatement,
        Component,
        otherwise
      )
    )
  );
